import type { ReactNode } from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  trend?: { value: number; label: string };
  onClick?: () => void;
}

export function StatCard({ title, value, subtitle, icon, iconBg, iconColor, trend, onClick }: StatCardProps) {
  return (
    <div
      onClick={onClick}
      className={`spa-card p-4 md:p-5 ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{title}</p>
          <p className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>{value}</p>
          {subtitle && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>
          )}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <span className="text-xs font-medium" style={{ color: trend.value >= 0 ? '#10B981' : '#EF4444' }}>
                {trend.value >= 0 ? '+' : ''}{trend.value}%
              </span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{trend.label}</span>
            </div>
          )}
        </div>
        <div
          className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0 ml-3"
          style={{ background: iconBg }}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${icon} text-lg`} style={{ color: iconColor }} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatusBadgeProps {
  status: string;
  children: ReactNode;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  'Bekliyor': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Teyit Edildi': { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
  'Geldi': { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Gelmedi': { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  'İptal Etti': { bg: 'rgba(156,163,175,0.12)', text: '#9CA3AF' },
  'Ödendi': { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Eksik Ödeme': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Açık Ödeme': { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  'Aktif': { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Pasif': { bg: 'rgba(156,163,175,0.12)', text: '#9CA3AF' },
  'Sorunlu': { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  'Yasaklı': { bg: 'rgba(26,26,26,0.12)', text: '#1A1A1A' },
  'Onay Bekliyor': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Onaylandı': { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Reddedildi': { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  'Randevuya Dönüştü': { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
  'Dikkat': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Etik Dışı Talep': { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  'İzlemede': { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
  'Uyarıda': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Yapıldı': { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Beklemede': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
};

export function StatusBadge({ status, children }: StatusBadgeProps) {
  const colors = statusColors[status] || { bg: 'rgba(156,163,175,0.12)', text: '#9CA3AF' };
  return (
    <span
      className="spa-badge"
      style={{ background: colors.bg, color: colors.text }}
    >
      {children}
    </span>
  );
}